import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import ReportGenerator from './components/report/form/ReportGenerator';
import ReportDisplay from './components/report/viewer/ReportDisplay';
import ReportHistory from './components/report/history/ReportHistory';
import CacheSuggestions from './components/report/controls/CacheSuggestions';
import JobsList from './components/report/history/JobsList';
import UserInfo from './components/auth/UserInfo';
import LeftNavPanel from './components/navigation/LeftNavPanel';
import CodeGenerator from './components/code/generator/CodeGenerator';
import CodeHistory from './components/code/history/CodeHistory';
import CodeJobsList from './components/code/history/CodeJobsList';
import CodeViewer from './components/code/viewer/CodeViewer';
import CodeGeneratorNav from './components/code/navigation/CodeGeneratorNav';
import SDLCNavigation from './components/sdlc/SDLCNavigation';
import PipelineWorkspace from './components/pipeline/PipelineWorkspace';
import IdeaStage from './components/sdlc/IdeaStage';
import RequirementsStage from './components/sdlc/RequirementsStage';
import ArchitectureStage from './components/sdlc/ArchitectureStage';
import CodeStage from './components/sdlc/CodeStage';
import TestsStage from './components/sdlc/TestsStage';
import CICDStage from './components/sdlc/CICDStage';
import DeploymentStage from './components/sdlc/DeploymentStage';
import DocsStage from './components/sdlc/DocsStage';
import NotificationContainer from './components/shared/NotificationContainer';
import IdeaList from './components/ideas/IdeaList';
import IdeaSubmission from './components/ideas/IdeaSubmission';
import IdeaDetail from './components/ideas/IdeaDetail';
import AdminPortal from './components/admin/AdminPortal';
import StudioLayout from './components/studio/StudioLayout';
import { setNotificationCallback } from './services/notification.service';
import { getUserEmail, clearAuthData } from './services/auth.service';
import { getReportById } from './services/history.service';
import { createIdea } from './services/idea.service';
import { isTokenExpired, getTokenExpiryInfo } from './utils/tokenManager';

function App() {
  const [userEmail, setUserEmail] = useState(null);
  const [cacheSuggestions, setCacheSuggestions] = useState(null);
  const [currentReport, setCurrentReport] = useState(null);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Setup notification system
  useEffect(() => {
    const handleNotification = (message, type = 'info', duration = 3000) => {
      const id = Date.now();
      setNotifications(prev => [...prev, { id, message, type, duration }]);
    };
    
    setNotificationCallback(handleNotification);
  }, []);

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  // Load user email on component mount
  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        const email = await getUserEmail();
        setUserEmail(email);
      } catch (error) {
        console.error('Failed to load user email:', error);
      }
    };

    loadUserEmail();
  }, []);

  // Check token expiry on mount and periodically
  useEffect(() => {
    const checkTokenExpiry = () => {
      if (isTokenExpired()) {
        console.warn('Token has expired, redirecting to login...');
        const { showWarning } = require('./services/notification.service');
        showWarning('Session expired. Please login again.', 3000);
        clearAuthData();
        setTimeout(() => navigate('/login'), 500);
        return;
      }
      
      // Log time remaining
      const expiryInfo = getTokenExpiryInfo();
      if (expiryInfo.hasToken && !expiryInfo.isExpired) {
        console.log(`Token expires in ${expiryInfo.remainingMinutes} minutes`);
        
        // Show warning if less than 1 minute remaining
        if (expiryInfo.remainingMinutes <= 1 && expiryInfo.remainingMinutes > 0) {
          const { showWarning } = require('./services/notification.service');
          showWarning(`Token will expire in ${expiryInfo.remainingMinutes} minute(s). Please refresh your session.`, 10000);
        }
      }
    };
    
    // Check immediately on mount
    checkTokenExpiry();
    
    // Check every minute
    const intervalId = setInterval(checkTokenExpiry, 60000);
    
    return () => clearInterval(intervalId);
  }, [navigate]);

  const handleReportGenerated = async (data, suggestions = null, needsApprovalFlag = false) => {
    if (suggestions && suggestions.length > 0) {
      // Show cache suggestions modal
      setCacheSuggestions(suggestions);
    } else if (data) {
      let ideaSynced = false;
      try {
        const sections = Array.isArray(data.sections) ? data.sections : [];
        const summary = sections.find((s) => s?.section_type === 'idea_summary')?.content || '';
        await createIdea({
          title: (data.title || data.idea || data.idea_title || 'Idea from Analyzer').trim(),
          description: data.summary || summary || data.idea || '',
          category: data.category || '',
          tags: Array.isArray(data.tags) ? data.tags : [],
          questionnaire_data: data.questionnaire_data || {},
          is_draft: false,
        });
        ideaSynced = true;
      } catch {
        // Non-blocking; report flow should continue even if idea sync fails.
      }

      // Store report and show it
      setCurrentReport({ ...data, _ideaSynced: ideaSynced });
      setNeedsApproval(needsApprovalFlag);
      
      // Navigate to new report display
      navigate('/tools/idea-analyzer/report/new');
    }
  };

  const handleSelectCachedReport = (report) => {
    setCacheSuggestions(null);
    const reportId = report._id || report.report_id;
    if (reportId) {
      navigate(`/tools/idea-analyzer/reports/${reportId}`);
    }
  };

  const handleGenerateNewFromSuggestions = () => {
    setCacheSuggestions(null);
    // Store flag to force regeneration bypassing cache
    sessionStorage.setItem('forceRegenerate', 'true');
    // User will need to click Generate Report button again
    // Or we could trigger it automatically here if we had access to the form
  };

  const handleCloseSuggestions = () => {
    setCacheSuggestions(null);
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/login');
  };

  // Dynamic title based on current route
  const getHeaderContent = () => {
    if (location.pathname.startsWith('/tools/code-generator')) {
      return {
        title: 'Code & Test Generator',
        subtitle: 'Generate production-ready code with comprehensive test coverage',
        badge: '🚧 Under Development'
      };
    }
    if (location.pathname.startsWith('/tools/sdlc')) {
      return {
        title: 'SDLC Workspace',
        subtitle: 'Idea → Requirements → Design → Docs → Code → Tests → CI/CD → Deploy',
        badge: '🧪 Beta'
      };
    }
    if (location.pathname.startsWith('/tools/pipeline')) {
      return {
        title: 'AI Delivery Pipeline',
        subtitle: 'Requirements → Design → Docs → Code → Tests → CI/CD → AWS Deploy',
        badge: '🤖 Agentic'
      };
    }    if (location.pathname.startsWith('/tools/studio')) {
      return {
        title: 'AI Studio',
        subtitle: 'Requirements Chatbot — AI-guided SDLC requirements gathering',
        badge: '🧠 Smart Chat',
      };
    }    if (location.pathname.startsWith('/admin')) {
      return {
        title: 'Admin Portal',
        subtitle: 'User management and platform administration',
      };
    }
    if (location.pathname.startsWith('/ideas')) {
      return {
        title: 'Idea Management',
        subtitle: 'Submit, evaluate and track ideas through the review workflow',
      };
    }
    // Default to Idea Analyzer
    return {
      title: 'Idea Analysis Report Generator',
      subtitle: 'Transform your ideas into comprehensive analysis reports'
    };
  };

  const headerContent = getHeaderContent();

  return (
    <div className="App">
      {/* Cache Suggestions Modal */}
      {cacheSuggestions && (
        <CacheSuggestions
          suggestions={cacheSuggestions}
          onSelect={handleSelectCachedReport}
          onGenerateNew={handleGenerateNewFromSuggestions}
          onClose={handleCloseSuggestions}
        />
      )}
      
      {/* Left Navigation Panel for Tools */}
      <LeftNavPanel isOpen={isNavOpen} onClose={() => setIsNavOpen(false)} />
      
      <header className="app-header">
        <div className="header-top">
          <button 
            className="hamburger-menu" 
            onClick={() => setIsNavOpen(!isNavOpen)}
            aria-label="Toggle navigation menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
          
          <div className="header-title">
            <div className="title-with-logo">
              <img src="/infin-logo.png" alt="Infin Logo" className="header-logo" />
              <div className="title-text">
                <div className="title-with-badge">
                  <h1>{headerContent.title}</h1>
                  {headerContent.badge && <span className="dev-badge">{headerContent.badge}</span>}
                </div>
                <p>{headerContent.subtitle}</p>
              </div>
            </div>
          </div>
          
          <UserInfo email={userEmail} onLogout={handleLogout} />
        </div>
        
        {/* Navigation for current tool */}
        {location.pathname.startsWith('/tools/idea-analyzer') && <IdeaAnalyzerNavigation />}
        {location.pathname.startsWith('/tools/code-generator') && <CodeGeneratorNav />}
        {location.pathname.startsWith('/tools/sdlc') && <SDLCNavigation />}
        {/* Pipeline has its own inline stepper — no separate nav component needed */}
      </header>

      <main className="main-content">
        <Routes>
          {/* Redirect root to AI Studio */}
          <Route path="/" element={<Navigate to="/tools/studio" replace />} />

          {/* ── Idea Management ───────────────────────────────────── */}
          <Route path="/ideas" element={<IdeaList />} />
          <Route path="/ideas/new" element={<IdeaSubmission />} />
          <Route path="/ideas/:ideaId" element={<IdeaDetail />} />
          <Route path="/ideas/:ideaId/edit" element={<IdeaSubmission />} />

          {/* ── Admin ─────────────────────────────────────────────── */}
          <Route path="/admin" element={<AdminPortal />} />
          <Route path="/admin/users" element={<Navigate to="/admin" replace />} />

          {/* ── Legacy Idea Analyzer Tool Routes ──────────────────── */}
          
          {/* Idea Analyzer Tool Routes */}
          <Route path="/tools/idea-analyzer" element={
            <ReportGenerator onReportGenerated={handleReportGenerated} />
          } />
          <Route path="/tools/idea-analyzer/report/new" element={
            <ReportDisplay 
              reportData={currentReport} 
              onNewReport={() => navigate('/tools/idea-analyzer')}
              needsApproval={needsApproval}
            />
          } />
          <Route path="/tools/idea-analyzer/favorites" element={
            <ReportHistory showFavorites={true} />
          } />
          <Route path="/tools/idea-analyzer/history" element={
            <ReportHistory showFavorites={false} />
          } />
          <Route path="/tools/idea-analyzer/jobs" element={
            <JobsList />
          } />
          <Route path="/tools/idea-analyzer/reports/:reportId" element={
            <ReportViewer />
          } />
          
          {/* Code & Test Generator Tool Routes */}
          <Route path="/tools/code-generator" element={
            <CodeGenerator />
          } />
          <Route path="/tools/code-generator/history" element={
            <CodeHistory showFavorites={false} />
          } />
          <Route path="/tools/code-generator/favorites" element={
            <CodeHistory showFavorites={true} />
          } />
          <Route path="/tools/code-generator/jobs" element={
            <CodeJobsList />
          } />
          <Route path="/tools/code-generator/codes/:codeId" element={
            <CodeViewer />
          } />
          
          {/* Legacy redirects for backwards compatibility */}
          <Route path="/report/new" element={<Navigate to="/tools/idea-analyzer/report/new" replace />} />
          <Route path="/favorites" element={<Navigate to="/tools/idea-analyzer/favorites" replace />} />
          <Route path="/history" element={<Navigate to="/tools/idea-analyzer/history" replace />} />
          <Route path="/jobs" element={<Navigate to="/tools/idea-analyzer/jobs" replace />} />
          <Route path="/reports/:reportId" element={<Navigate to="/tools/idea-analyzer/reports/:reportId" replace />} />

          {/* ── AI Delivery Pipeline ───────────────────────────────── */}
          <Route path="/tools/pipeline/*" element={<PipelineWorkspace />} />

          {/* ── AI Studio (SDLC Requirements Chatbot) ─────────────── */}
          <Route path="/tools/studio" element={<StudioLayout />} />
          <Route path="/tools/studio/:projectId" element={<StudioLayout />} />

          {/* SDLC Workspace Routes */}
          <Route path="/tools/sdlc" element={<Navigate to="/tools/sdlc/idea" replace />} />
          <Route path="/tools/sdlc/idea" element={<IdeaStage />} />
          <Route path="/tools/sdlc/requirements" element={<RequirementsStage />} />
          <Route path="/tools/sdlc/requirements/:reportId" element={<ReportViewer />} />
          <Route path="/tools/sdlc/architecture" element={<ArchitectureStage />} />
          <Route path="/tools/sdlc/docs" element={<DocsStage />} />
          <Route path="/tools/sdlc/code" element={<CodeStage />} />
          <Route path="/tools/sdlc/code/:codeId" element={<CodeViewer />} />
          <Route path="/tools/sdlc/tests" element={<TestsStage />} />
          <Route path="/tools/sdlc/cicd" element={<CICDStage />} />
          <Route path="/tools/sdlc/deployment" element={<DeploymentStage />} />
        </Routes>
      </main>

      {/* Notification Container */}
      <NotificationContainer 
        notifications={notifications}
        onClose={removeNotification}
      />
    </div>
  );
}

// Navigation Component for Idea Analyzer Tool
function IdeaAnalyzerNavigation() {
  const navigate = useNavigate();
  const location = window.location.pathname;
  
  // Only show navigation when in idea analyzer tool
  if (!location.startsWith('/tools/idea-analyzer')) {
    return null;
  }

  return (
    <div className="app-navigation">
      <button 
        className={`nav-btn ${location === '/tools/idea-analyzer/favorites' ? 'active' : ''}`}
        onClick={() => navigate('/tools/idea-analyzer/favorites')}
      >
        ⭐ Favorites
      </button>
      <button 
        className={`nav-btn ${location === '/tools/idea-analyzer' ? 'active' : ''}`}
        onClick={() => navigate('/tools/idea-analyzer')}
      >
        ✨ New Report
      </button>
      <button 
        className={`nav-btn ${location === '/tools/idea-analyzer/jobs' ? 'active' : ''}`}
        onClick={() => navigate('/tools/idea-analyzer/jobs')}
      >
        ⚙️ Jobs
      </button>
      <button 
        className={`nav-btn ${location === '/tools/idea-analyzer/history' ? 'active' : ''}`}
        onClick={() => navigate('/tools/idea-analyzer/history')}
      >
        📚 History
      </button>
    </div>
  );
}

// Report Viewer Component with Route Params
function ReportViewer() {
  const { reportId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsApproval, setNeedsApproval] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        
        // Check if this is a job report from sessionStorage first
        const jobReportKey = `jobReport_${reportId}`;
        const jobReportData = sessionStorage.getItem(jobReportKey);
        if (jobReportData) {
          const { report, needsApproval: jobNeedsApproval } = JSON.parse(jobReportData);
          setReportData(report);
          setNeedsApproval(jobNeedsApproval || false);
          setLoading(false);
          return; // Don't remove from sessionStorage yet - keep it for page refreshes
        }
        
        // Check if this is a temp report from sessionStorage (legacy)
        const tempReport = sessionStorage.getItem('tempReport');
        if (tempReport) {
          sessionStorage.removeItem('tempReport');
          setReportData(JSON.parse(tempReport));
          setNeedsApproval(false);
          setLoading(false);
          return;
        }
        
        // Otherwise, fetch from database
        const response = await getReportById(reportId);
        if (response.success && response.report) {
          setReportData(response.report);
          setNeedsApproval(false); // Already saved to DB
        } else {
          setError('Report not found');
        }
      } catch (err) {
        console.error('Failed to load report:', err);
        setError('Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      loadReport();
    }
  }, [reportId]);

  const handleNewReport = () => {
    // Clean up job report data when navigating away
    sessionStorage.removeItem(`jobReport_${reportId}`);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading report...</p>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="error-container">
        <p className="error-message">{error || 'Report not found'}</p>
        <button className="btn-primary" onClick={handleNewReport}>
          Go to Home
        </button>
      </div>
    );
  }

  return (
    <ReportDisplay 
      reportData={reportData}
      onNewReport={handleNewReport}
      needsApproval={needsApproval}
    />
  );
}

export default App;
